import "../../utils/tailwind.css";
import { PageProvider, useAddServiceProvider } from "./provider";
import { useState } from "react";
import Label from "../../components/label";
import Section from "../../components/section";
import Input, { FormikInput } from "../../components/input";
import Button from "../../components/Buttons";
import { Formik, Form, FieldArray, ErrorMessage } from "formik";
import ReactQuill from "react-quill";
import Switch from "../../components/switch";
import ImageUploader from "../../components/upload-image";
import Plus from "../../components/svg/plus";
import Trash from "../../components/svg/trash";
import slugify from "slugify";
import * as Yup from "yup";
import ScrolltoFormError from "../../components/scroll-to-form-error";
import { useNavigate } from "react-router-dom";

export default function AddServicePage() {
  return (
    <div className="p-3">
      <PageProvider>
        <PageContent />
      </PageProvider>
    </div>
  );
}

function PageContent() {
  const { initialValues, handleSubmit, isEditing, isLoading } = useAddServiceProvider();
  const [isSlugEdited, setIsSlugEdited] = useState(false);
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    title: Yup.string().required("Heading is required.").max(150, "Title must be at most 150 characters"),
    slug_url: Yup.string().required("Slug URL is required."),
    subtitle: Yup.string().required("Subtitle is required."),
    overview: Yup.string().required("Overview is required."),
    challenge: Yup.string().required("Challenge is required."),
    solution: Yup.string().required("Solution is required."),
  });

  if (isLoading) {
    return (
      <Section>
        <div className="text-center py-10 text-gray-500">Loading service details...</div>
      </Section>
    );
  }

  return (
    <Section className="h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <Label label={isEditing ? "Edit Solution Service Details" : "Add Solution Service Details"} className="text-[18px] text-black" />
        <button
          type="button"
          className="text-sm px-4 py-2 border rounded-md hover:bg-gray-100"
          onClick={() => navigate("/services")}>
          Back to List
        </button>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => (
          <Form>
            <ScrolltoFormError>
              {/* Published Switch */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <div className="flex items-center space-x-2">
                  <Label label="Is Published ?" className="text-black mr-4 mt-3 text-[15px]" />
                  <Switch
                    className="pt-1"
                    type="switch"
                    name="is_publish"
                    onChange={(val) => setFieldValue("is_publish", val)}
                    checked={values.is_publish}
                  />
                </div>
              </div>

              {/* Title, Slug & Subtitle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div className="flex flex-col">
                  <Label label="Service Heading / Title" className="mb-2 text-black text-[15px]" />
                  <FormikInput
                    name="title"
                    rootClassName="w-full"
                    value={values.title || ""}
                    onChange={(e) => {
                      const newTitle = e.target.value;
                      setFieldValue("title", newTitle);
                      if (!isSlugEdited) {
                        setFieldValue("slug_url", slugify(newTitle, { lower: true, strict: true }));
                      }
                    }}
                  />
                </div>

                <div className="flex flex-col">
                  <Label label="Slug URL" className="mb-2 text-black text-[15px]" />
                  <FormikInput
                    name="slug_url"
                    rootClassName="w-full"
                    value={values.slug_url || ""}
                    onChange={(e) => {
                      setIsSlugEdited(true);
                      setFieldValue("slug_url", e.target.value);
                    }}
                  />
                </div>

                <div className="flex flex-col">
                  <Label label="Keywords" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="keywords" rootClassName="w-full" placeholder="e.g. Next.js, SEO, UI/UX" />
                </div>
              </div>

              <div className="grid grid-cols-1 mt-2">
                <div className="flex flex-col">
                  <Label label="Tagline / Subtitle" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="subtitle" rootClassName="w-full" />
                </div>
              </div>

              {/* Card Background / Hero Banner Image */}
              <div className="grid grid-cols-1 mt-3">
                <div className="flex flex-col">
                  <Label label="Card BG Image (Used as Hero Banner on detail page)" className="mb-2 text-black text-[15px]" />
                  <div className="w-[200px]">
                    <ImageUploader
                      imageUrl={values.bg_img}
                      onChange={(res) => {
                        setFieldValue(`bg_img`, res?.data?.file_url);
                      }}
                    />
                  </div>
                  <ErrorMessage name="bg_img">
                    {(msg) => <div className="form-error text-red-500 mt-1 text-xs">{msg}</div>}
                  </ErrorMessage>
                </div>
              </div>

              {/* Rich Editors for Text Blocks */}
              <div className="w-full mt-4">
                <div className="flex flex-col mb-3">
                  <Label label="Service Overview / Description" className="mb-2 text-black text-[15px]" />
                  <ReactQuill theme="snow" value={values.overview} onChange={(e) => setFieldValue("overview", e)} />
                  <ErrorMessage name="overview">
                    {(msg) => <div className="form-error text-red-500 mt-1 text-xs">{msg}</div>}
                  </ErrorMessage>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col mb-3">
                    <Label label="The Challenge" className="mb-2 text-black text-[15px]" />
                    <ReactQuill theme="snow" value={values.challenge} onChange={(e) => setFieldValue("challenge", e)} />
                    <ErrorMessage name="challenge">
                      {(msg) => <div className="form-error text-red-500 mt-1 text-xs">{msg}</div>}
                    </ErrorMessage>
                  </div>

                  <div className="flex flex-col mb-3">
                    <Label label="Our Solution / Approach" className="mb-2 text-black text-[15px]" />
                    <ReactQuill theme="snow" value={values.solution} onChange={(e) => setFieldValue("solution", e)} />
                    <ErrorMessage name="solution">
                      {(msg) => <div className="form-error text-red-500 mt-1 text-xs">{msg}</div>}
                    </ErrorMessage>
                  </div>
                </div>
              </div>

              {/* Highlight Metrics */}
              <div className="w-full mt-5 border-t pt-4">
                <Label label="Highlight Metrics" className="mb-2 text-black font-semibold text-[15px]" />
                <FieldArray name="metrics">
                  {({ push, remove }) => (
                    <div className="space-y-3">
                      {values.metrics?.map((metric, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="flex-1">
                            <Input
                              placeholder="Value (e.g. +45% or 98%)"
                              value={metric.value}
                              onChange={(e) => setFieldValue(`metrics.${idx}.value`, e.target.value)}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder="Label (e.g. Conversion Lift)"
                              value={metric.label}
                              onChange={(e) => setFieldValue(`metrics.${idx}.label`, e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            onClick={() => remove(idx)}>
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="mt-2 text-sm bg-black text-white px-3 py-1.5 rounded flex items-center gap-2"
                        onClick={() => push({ label: "", value: "" })}>
                        <Plus className="w-3 h-3 text-white" />
                        <span>Add Metric</span>
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>

              {/* Core Offerings & Deliverables */}
              <div className="w-full mt-5 border-t pt-4">
                <div className="flex flex-col mb-3">
                  <Label label="Offerings Section Title" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="key_features_heading" rootClassName="w-full" placeholder="e.g. Core Offerings & Deliverables" />
                </div>
                <Label label="Core Offerings & Deliverables" className="mb-2 text-black font-semibold text-[15px]" />
                <FieldArray name="key_features">
                  {({ push, remove }) => (
                    <div className="space-y-3">
                      {values.key_features?.map((feat, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="flex-1">
                            <Input
                              placeholder="Title (e.g. Custom UI/UX)"
                              value={feat.title}
                              onChange={(e) => setFieldValue(`key_features.${idx}.title`, e.target.value)}
                            />
                          </div>
                          <div className="flex-2 flex-grow">
                            <Input
                              placeholder="Short Description"
                              value={feat.description}
                              onChange={(e) => setFieldValue(`key_features.${idx}.description`, e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            onClick={() => remove(idx)}>
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="mt-2 text-sm bg-black text-white px-3 py-1.5 rounded flex items-center gap-2"
                        onClick={() => push({ title: "", description: "" })}>
                        <Plus className="w-3 h-3 text-white" />
                        <span>Add Offering</span>
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>

              {/* Process Timelines */}
              <div className="w-full mt-5 border-t pt-4">
                <div className="flex flex-col mb-3">
                  <Label label="Process Section Title" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="process_heading" rootClassName="w-full" placeholder="e.g. Our Process Workflow" />
                </div>
                <Label label="Process Timeline" className="mb-2 text-black font-semibold text-[15px]" />
                <FieldArray name="process">
                  {({ push, remove }) => (
                    <div className="space-y-3">
                      {values.process?.map((proc, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-[80px]">
                            <Input
                              placeholder="Step (e.g. 01)"
                              value={proc.step}
                              onChange={(e) => setFieldValue(`process.${idx}.step`, e.target.value)}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder="Title (e.g. Discovery)"
                              value={proc.title}
                              onChange={(e) => setFieldValue(`process.${idx}.title`, e.target.value)}
                            />
                          </div>
                          <div className="flex-2 flex-grow">
                            <Input
                              placeholder="Description"
                              value={proc.desc}
                              onChange={(e) => setFieldValue(`process.${idx}.desc`, e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            onClick={() => remove(idx)}>
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="mt-2 text-sm bg-black text-white px-3 py-1.5 rounded flex items-center gap-2"
                        onClick={() => push({ step: `0${(values.process?.length || 0) + 1}`, title: "", desc: "" })}>
                        <Plus className="w-3 h-3 text-white" />
                        <span>Add Process Step</span>
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>

              {/* Results Checklist */}
              <div className="w-full mt-5 border-t pt-4 mb-24">
                <div className="flex flex-col mb-3">
                  <Label label="Results Section Title" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="results_heading" rootClassName="w-full" placeholder="e.g. Expected Business Impact" />
                </div>
                <Label label="Business Impact (Checklist)" className="mb-2 text-black font-semibold text-[15px]" />
                <FieldArray name="results">
                  {({ push, remove }) => (
                    <div className="space-y-3">
                      {values.results?.map((res, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="text-gray-400 font-bold text-sm">#{idx + 1}</span>
                          <div className="flex-1">
                            <Input
                              placeholder="e.g. 60% faster page loading times across all devices."
                              value={res}
                              onChange={(e) => setFieldValue(`results.${idx}`, e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            onClick={() => remove(idx)}>
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="mt-2 text-sm bg-black text-white px-3 py-1.5 rounded flex items-center gap-2"
                        onClick={() => push("")}>
                        <Plus className="w-3 h-3 text-white" />
                        <span>Add Result checklist item</span>
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>

              {/* Save Bar */}
              <div className="fixed bottom-0 left-0 w-full bg-white flex justify-end items-center py-3 pr-4 space-x-3 shadow-md z-30">
                <Button type="submit">Save</Button>
              </div>
            </ScrolltoFormError>
          </Form>
        )}
      </Formik>
    </Section>
  );
}
